import { MouseEventHandler, useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';

import { useGetCurrentUserQuery } from '../../store/user/userApi';
import { removeFromStorage } from '../../utils/webStorage';
import { useTheme } from '../../utils/theme-context';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { tryToFetchKnowledgeBaseUnreadCount } from '../../store/sofia/notifications/knowledgeBaseNotificationsSlice';
import KnowledgeBaseNotificationsModal from './KnowledgeBaseNotificationsModal';


interface INavigation {
    search?: string;
    closeSidebar?: () => void;
}

const allPages = [
    { name: 'Dashboard', url: '/dashboard', icon: '/assets/navigation-icons/dashboard.svg', iconWhite: '/assets/navigation-icons/dashboard-white.svg', activeIcon: '/assets/navigation-icons/dashboard-active.svg' },
    { name: 'Users', url: '/users', icon: '/assets/navigation-icons/clients.svg', iconWhite: '/assets/navigation-icons/clients-white.svg', activeIcon: '/assets/navigation-icons/clients-active.svg' },
    { name: 'Agent Clients', url: '/agent-clients', icon: '/assets/navigation-icons/agents.svg', iconWhite: '/assets/navigation-icons/agents-white.svg', activeIcon: '/assets/navigation-icons/agents-active.svg' },
    // { name: 'Agents', url: '/agents', icon: '/assets/navigation-icons/clients.svg', iconWhite: '/assets/navigation-icons/clients-white.svg', activeIcon: '/assets/navigation-icons/clients-active.svg' },
    // { name: 'Clients', url: '/clients', icon: '/assets/navigation-icons/agents.svg', iconWhite: '/assets/navigation-icons/agents-white.svg', activeIcon: '/assets/navigation-icons/agents-active.svg' },
    // { name: 'Contracts', url: '/contracts', icon: '/assets/navigation-icons/contracts.svg', iconWhite: '/assets/navigation-icons/contracts-white.svg', activeIcon: '/assets/navigation-icons/contracts-active.svg' },
]

const Navigation = ({ search, closeSidebar }: INavigation) => {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [notifModalOpen, setNotifModalOpen] = useState(false);

    const { data: userInfo } = useGetCurrentUserQuery();

    const unreadCount = useAppSelector(
        (state) => state.knowledgeBaseNotifications.unreadCount,
    );

    const handleLogout = () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        removeFromStorage('accessToken');
        removeFromStorage('refreshToken');
        removeFromStorage('roles');
        removeFromStorage('user');
        window.location.reload();
        // (window as any).Intercom('shutdown');
        navigate('/login');
    };

    useEffect(() => {
        // initial load
        dispatch(tryToFetchKnowledgeBaseUnreadCount());

        const id = setInterval(() => {
            dispatch(tryToFetchKnowledgeBaseUnreadCount());
        }, 6000);

        return () => clearInterval(id);
    }, [dispatch]);

    const handleBadgeClick: MouseEventHandler<HTMLDivElement> = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setNotifModalOpen(true);
    };

    return (
        <div className="flex flex-col min-h-[40vh] justify-between ">
            <div className="flex flex-row items-center justify-between mb-6 mt-2 px-0">
                <div className="flex flex-row items-center">
                    <img
                        className="w-10 h-10 sm:w-10 sm:h-10 bg-black dark:bg-white rounded-full mr-2"
                        src="/svg/layout/hotel-avatar.svg"
                        alt="main photo"
                    />
                    <div className="text-left">
                        <div className="text-sm sm:text-base font-normal dark:text-primary-light text-dark w-[120px] break-words">
                            {userInfo?.username || ''}
                        </div>
                        <p className="text-xs sm:text-sm text-primary-light">
                            Administrator
                        </p>
                    </div>
                </div>
                <div className="flex flex-row items-center justify-end">
                    <NavLink
                        className="cursor-pointer hover:opacity-80"
                        to={'/models'}>
                        <img
                            src={
                                theme === 'dark'
                                    ? '/assets/navigation-icons/sidebar/settings-icon-white.svg'
                                    : '/assets/navigation-icons/sidebar/settings-icon.svg'
                            }
                            className="object-contain w-5 h-5 sm:w-[23px] sm:h-[23px] mr-2"
                        />
                    </NavLink>
                    <div
                        className="cursor-pointer hover:opacity-80"
                        onClick={handleLogout}>
                        <img
                            src={
                                theme === 'dark'
                                    ? '/assets/navigation-icons/sidebar/logout-icon-white.svg'
                                    : '/assets/navigation-icons/sidebar/logout-icon.svg'
                            }
                            className="object-contain w-5 h-5 sm:w-[23px] sm:h-[23px]"
                        />
                    </div>
                </div>
            </div>

            <div className="border-t py-3 px-3 sm:px-4">
                <p className='dark:text-primary-darkMode text-sm mb-5'>Menu</p>

                {allPages?.map((item: any, idx: number) => {
                    return (
                        <NavLink
                            key={`${item?.name}-${idx}`}
                            to={item?.url}
                            className={({ isActive }) => `
      flex flex-row items-center cursor-pointer hover:opacity-80 px-1 sm:px-2 py-2 mb-2 rounded-md
      ${isActive ? 'bg-[#f3fded]' : ''}
  `}
                        >
                            {({ isActive }) => (
                                <>
                                    <img
                                        src={
                                            isActive ? item?.activeIcon :
                                                (theme === 'dark')
                                                    ? item?.iconWhite
                                                    : item?.icon
                                        }
                                        className="object-contain w-[20px] mr-2"
                                    />

                                    <p
                                        className={`
            text-sm sm:text-base
            ${isActive ? 'text-[#00330F]' : 'text-[#252B37] dark:text-primary-dark'}
        `}
                                    >
                                        {item?.name}
                                    </p>
                                </>
                            )}
                        </NavLink>
                    )
                })}
                <NavLink
                    to="/expert-partners"
                    className={({ isActive }) => `
      flex flex-row items-center cursor-pointer hover:opacity-80 px-1 sm:px-2 py-2 mb-2 rounded-md
      ${isActive ? 'bg-[#f3fded]' : ''}
  `}
                >
                    {({ isActive }) => (
                        <>
                            <img
                                src={
                                    (theme === 'dark' && !isActive)
                                        ? '/assets/navigation/checkbook-white.svg'
                                        : '/assets/navigation/checkbook.svg'
                                }
                                className="object-contain w-[20px] mr-2"
                            />

                            <p
                                className={`
            text-sm sm:text-base
            ${isActive ? 'text-[#00330F]' : 'text-[#252B37] dark:text-primary-dark'}
        `}
                            >
                                Expert Partners
                            </p>
                        </>
                    )}
                </NavLink>
                <NavLink
                    to="/demo-requests"
                    className={({ isActive }) => `
      flex flex-row items-center cursor-pointer hover:opacity-80 px-1 sm:px-2 py-2 mb-2 rounded-md
      ${isActive ? 'bg-[#f3fded]' : ''}
  `}
                >
                    {({ isActive }) => (
                        <>
                            <img
                                src={
                                    (theme === 'dark' && !isActive)
                                        ? '/assets/navigation/checkbook-white.svg'
                                        : '/assets/navigation/checkbook.svg'
                                }
                                className="object-contain w-[20px] mr-2"
                            />

                            <p
                                className={`
            text-sm sm:text-base
            ${isActive ? 'text-[#00330F]' : 'text-[#252B37] dark:text-primary-dark'}
        `}
                            >
                                Early Access
                            </p>
                        </>
                    )}
                </NavLink>
                <NavLink
                    to="/claire-glen-ivy"
                    className={({ isActive }) => `
      flex flex-row items-center cursor-pointer hover:opacity-80 px-1 sm:px-2 py-2 mb-2 rounded-md
      ${isActive ? 'bg-[#f3fded]' : ''}
  `}
                >
                    {({ isActive }) => (
                        <>
                            <img
                                src={
                                    (theme === 'dark' && !isActive)
                                        ? '/assets/navigation/checkbook-white.svg'
                                        : '/assets/navigation/checkbook.svg'
                                }
                                className="object-contain w-[20px] mr-2"
                            />

                            <p
                                className={`
            text-sm sm:text-base
            ${isActive ? 'text-[#00330F]' : 'text-[#252B37] dark:text-primary-dark'}
        `}
                            >
                                Claire - Glen Ivy:
                            </p>
                        </>
                    )}
                </NavLink>
                <NavLink
                    to="/costs"
                    className={({ isActive }) => `
      flex flex-row items-center cursor-pointer hover:opacity-80 px-1 sm:px-2 py-2 mb-2 rounded-md
      ${isActive ? 'bg-[#f3fded]' : ''}
  `}
                >
                    {({ isActive }) => (
                        <>
                            <img
                                src={
                                    (theme === 'dark' && !isActive)
                                        ? '/assets/navigation-icons/dashboard-white.svg'
                                        : '/assets/navigation-icons/dashboard.svg'
                                }
                                className="object-contain w-[20px] mr-2"
                            />

                            <p
                                className={`
            text-sm sm:text-base
            ${isActive ? 'text-[#00330F]' : 'text-[#252B37] dark:text-primary-dark'}
        `}
                            >
                                Leo LLM Costs
                            </p>
                        </>
                    )}
                </NavLink>
                <NavLink
                    to="/subscriptions"
                    className={({ isActive }) => `
      flex flex-row items-center cursor-pointer hover:opacity-80 px-1 sm:px-2 py-2 mb-2 rounded-md
      ${isActive ? 'bg-[#f3fded]' : ''}
  `}
                >
                    {({ isActive }) => (
                        <>
                            <img
                                src={
                                    (theme === 'dark' && !isActive)
                                        ? '/assets/navigation/checkbook-white.svg'
                                        : '/assets/navigation/checkbook.svg'
                                }
                                className="object-contain w-[20px] mr-2"
                            />

                            <p
                                className={`
            text-sm sm:text-base
            ${isActive ? 'text-[#00330F]' : 'text-[#252B37] dark:text-primary-dark'}
        `}
                            >
                                Leo Subscriptions
                            </p>
                        </>
                    )}
                </NavLink>
                <NavLink
                    to="/comparison-boards"
                    className={({ isActive }) => `
      flex flex-row items-center cursor-pointer hover:opacity-80 px-1 sm:px-2 py-2 mb-2 rounded-md
      ${isActive ? 'bg-[#f3fded]' : ''}
  `}
                >
                    {({ isActive }) => (
                        <>
                            <img
                                src={
                                    (theme === 'dark' && !isActive)
                                        ? '/assets/navigation/checkbook-white.svg'
                                        : '/assets/navigation/checkbook.svg'
                                }
                                className="object-contain w-[20px] mr-2"
                            />

                            <p
                                className={`
            text-sm sm:text-base
            ${isActive ? 'text-[#00330F]' : 'text-[#252B37] dark:text-primary-dark'}
        `}
                            >
                                Leo Comparison Boards
                            </p>
                        </>
                    )}
                </NavLink>
                <NavLink
                    to="/leo-chat"
                    className={({ isActive }) => `
      flex flex-row items-center cursor-pointer hover:opacity-80 px-1 sm:px-2 py-2 mb-2 rounded-md
      ${isActive ? 'bg-[#f3fded]' : ''}
  `}
                >
                    {({ isActive }) => (
                        <>
                            <img
                                src={
                                    (theme === 'dark' && !isActive)
                                        ? '/svg/icons/chat-icon-white.svg'
                                        : '/svg/icons/chat.svg'
                                }
                                className="object-contain w-[20px] mr-2"
                            />

                            <p
                                className={`
            text-sm sm:text-base
            ${isActive ? 'text-[#00330F]' : 'text-[#252B37] dark:text-primary-dark'}
        `}
                            >
                                Leo Chat
                            </p>
                        </>
                    )}
                </NavLink>
                <NavLink
                    to="/sofia-exceptions"
                    className={({ isActive }) => `
                              flex flex-row items-center cursor-pointer hover:opacity-80 px-1 sm:px-2 py-2 mb-3 rounded-md
                              ${isActive ? 'bg-[#f3fded]' : ''}
                            `}>
                    {({ isActive }) => (
                        <>
                            <img
                                src={
                                    theme === 'dark' && !isActive
                                        ? '/assets/navigation/checkbook-white.svg'
                                        : '/assets/navigation/checkbook.svg'
                                }
                                className="object-contain w-[20px] mr-2"
                            />

                            <div className="flex flex-1 items-center justify-between">
                                <p
                                    className={`
                                      text-sm sm:text-base
                                      ${isActive
                                            ? 'text-[#00330F]'
                                            : 'text-[#252B37] dark:text-primary-dark'
                                        }
                                    `}>
                                    Sofia
                                </p>

                                {unreadCount > 0 && (
                                    <div
                                        onClick={handleBadgeClick}
                                        className="relative ml-2 inline-flex items-center justify-center rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-semibold text-white shadow-sm">
                                        {unreadCount > 99
                                            ? '99+'
                                            : unreadCount}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </NavLink>
                <NavLink
                    to="/marketing"
                    className={({ isActive }) => `
                              flex flex-row items-center cursor-pointer hover:opacity-80 px-1 sm:px-2 py-2 mb-3 rounded-md
                              ${isActive ? 'bg-[#f3fded]' : ''}
                            `}>
                    <>
                        <img
                            src='/assets/navigation/marketing.svg'
                            className="object-contain w-[24px] mr-2"
                        />

                        <div className="flex flex-1 items-center justify-between">
                            <p
                                className={`
                                      text-sm sm:text-base text-[#252B37] dark:text-primary-dark`}>
                                Marketing
                            </p>
                        </div>
                    </>
                </NavLink>
                <NavLink
                    to="/prompts"
                    className={({ isActive }) => `
      flex flex-row items-center cursor-pointer hover:opacity-80 px-1 sm:px-2 py-2 mb-2 rounded-md
      ${isActive ? 'bg-[#f3fded]' : ''}
  `}
                >
                    {({ isActive }) => (
                        <>
                            <img
                                src={
                                    theme === 'dark' && !isActive
                                        ? '/assets/navigation/checkbook-white.svg'
                                        : '/assets/navigation/checkbook.svg'
                                }
                                className="object-contain w-[20px] mr-2"
                            />
                            <p
                                className={`
            text-sm sm:text-base
            ${isActive ? 'text-[#00330F]' : 'text-[#252B37] dark:text-primary-dark'}
        `}
                            >
                                Prompts
                            </p>
                        </>
                    )}
                </NavLink>
                <NavLink
                    to="/need-help"
                    className={({ isActive }) => `
      flex flex-row items-center cursor-pointer hover:opacity-80 px-1 sm:px-2 py-2 mb-2 rounded-md
      ${isActive ? 'bg-[#f3fded]' : ''}
  `}
                >
                    {({ isActive }) => (
                        <>
                            <img
                                src={
                                    theme === 'dark' && !isActive
                                        ? '/assets/navigation/checkbook-white.svg'
                                        : '/assets/navigation/checkbook.svg'
                                }
                                className="object-contain w-[20px] mr-2"
                            />

                            <p
                                className={`
            text-sm sm:text-base
            ${isActive ? 'text-[#00330F]' : 'text-[#252B37] dark:text-primary-dark'}
        `}
                            >
                                Help Requests
                            </p>
                        </>
                    )}
                </NavLink>
                <NavLink
                    to="/tips-feedback"
                    className={({ isActive }) => `
      flex flex-row items-center cursor-pointer hover:opacity-80 px-1 sm:px-2 py-2 mb-2 rounded-md
      ${isActive ? 'bg-[#f3fded]' : ''}
  `}
                >
                    {({ isActive }) => (
                        <>
                            <img
                                src={
                                    theme === 'dark' && !isActive
                                        ? '/assets/navigation/checkbook-white.svg'
                                        : '/assets/navigation/checkbook.svg'
                                }
                                className="object-contain w-[20px] mr-2"
                            />

                            <p
                                className={`
            text-sm sm:text-base
            ${isActive ? 'text-[#00330F]' : 'text-[#252B37] dark:text-primary-dark'}
        `}
                            >
                                Leo Tips Feedback
                            </p>
                        </>
                    )}
                </NavLink>
            </div>
            <div>

                {/* <img alt='hotelworld logo' className='' src='/assets/chat/hotelworld-logo.svg' /> */}
            </div>
            <KnowledgeBaseNotificationsModal
                isOpen={notifModalOpen}
                onClose={() => setNotifModalOpen(false)}
            />
        </div>
    );
};

export default Navigation;
